<template>
  <v-dialog
    v-model="show"
    scrollable
    width="1050"
    @keydown.esc="show = false"
  >

    <v-card>
      <v-card-title>
        <v-layout wrap>
          <v-btn text absolute right icon @click="show = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-flex sm11>
            <h3 class="headline mb-0">{{ $t('EXPORT_CONFIGURATION') }}
              <span class="subheading">({{ $t('EXPORT_NUMBER_INCLUDED', [numSelected]) }})</span>
              <v-btn :text="!advanced" color="info" @click="advanced = !advanced"><v-icon v-if="advanced">mdi-check</v-icon>{{ $t('EXPORT_EXPERT_SETTINGS') }}</v-btn>
            </h3>
          </v-flex>
        </v-layout>
      </v-card-title>
      <v-card-text>
        <v-layout wrap>

          <v-flex xs12>
            <v-select
              v-model="exportConfiguration"
              :items="exportConfigurationItems"
              :label="$t('EXPORT_SELECT_CONFIG')"
              return-object
              clearable
            ></v-select>
          </v-flex>
          <v-flex xs12>
            <v-alert type="error" dismissible :value="showAlert">
              {{alertText}}
            </v-alert>
          </v-flex>

          <template v-if="!advanced">
            <v-flex sm12 md6>

              <v-list>
                <v-list-group
                  v-for="(group, idx) in availableFields"
                  :key="idx"
                  v-model="groups[group.id]"
                  :prepend-icon="group.icon"
                  no-action
                >
                  <template v-slot:activator>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>{{ group.header }} <span v-if="selectedAvailableFields.filter(a => a.group === group.id).length" class="body-1">({{ $t('EXPORT_NUM_SELECTED_FIELDS', [selectedAvailableFields.filter(a => a.group === group.id).length]) }})</span></v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                  <v-list-item
                    v-if="!group.items.length"
                  >
                    <v-list-item-content>
                      <v-list-item-subtitle>{{ $t('EXPORT_HINT_ALL_INCLUDED') }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item
                    v-for="item in group.items"
                    :key="item.id"
                    @click="onAvailableToggle(item)"
                    ripple
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{ item.label }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn v-if="isAvailableSelected(item)" icon @click.stop="onMoveRight()" color="primary"><v-icon>mdi-chevron-right</v-icon></v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list-group>
              </v-list>

            </v-flex>
            <v-flex sm12 md6>

              <v-list class="fields">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>{{ $t('EXPORT_FORMAT') }} ({{ exportFields.length }}) <span v-if="selectedExportFields.length" class="body-1">({{ $t('EXPORT_NUM_SELECTED_FIELDS', [selectedExportFields.length]) }})</span></v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <!-- use [vuedraggable npm](https://github.com/SortableJS/Vue.Draggable) here, e.g.: https://codepen.io/abhaywawale/pen/PaVvyR?editors=1010 -->
                <!-- thanks to https://medium.com/vuetify/drag-n-drop-in-vuetify-part-i-166f2c7a4a82 for draggable -->
                <v-list-item
                  v-if="!exportFields.length"
                >
                  <v-list-item-content>
                    <v-list-item-subtitle>{{ $t('EXPORT_HINT_INCLUDE') }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  v-for="(item, idx) in exportFields"
                  :key="idx"
                  @click="onExportToggle(item)"
                  ripple
                >
                  <v-list-item-action>
                    <v-btn v-if="isExportSelected(item)" icon @click.stop="onMoveLeft()" color="primary"><v-icon>mdi-chevron-left</v-icon></v-btn>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.label }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>

            </v-flex>
          </template>
          <template v-else>
            <v-flex sm12>
              <v-sheet color="grey lighten-4">
                <v-container grid-list-md>
                  <h4 class="subheader">{{ $t('EXPORT_EXPERT_SETTINGS') }}</h4>

                  <v-layout wrap>
                    <v-flex sm12>
                      <v-select
                        v-model="encoding"
                        :items="encodingOptions"
                        :label="$t('EXPORT_ENCODING')"
                      ></v-select>
                    </v-flex>

                    <v-flex sm12 md6 v-if="!isSeparatorCustomBetween(separatorBetween)">
                      <v-select
                        :value="separatorBetween"
                        @input="onSeparatorBetween"
                        :items="separatorOptionsBetween"
                        :label="$t('EXPORT_SEPARATOR_BETWEEN')"
                      ></v-select>
                    </v-flex>
                    <v-flex sm8 md4 v-if="isSeparatorCustomBetween(separatorBetween)">
                      <v-select
                        :value="$t('EXPORT_SEPARATOR_FREE_CHAR')"
                        @input="onSeparatorBetween"
                        :items="separatorOptionsBetween"
                        :label="$t('EXPORT_SEPARATOR_BETWEEN')"
                      ></v-select>
                    </v-flex>
                    <v-flex sm4 md2 v-if="isSeparatorCustomBetween(separatorBetween)">
                      <v-text-field
                        :value="separatorBetween"
                        @input="onSeparatorBetween"
                        :label="$t('EXPORT_SEPARATOR_FREE_CHAR_LABEL')"
                        maxlength="1"
                      ></v-text-field>
                    </v-flex>

                    <v-flex sm12 md6 v-if="!isSeparatorCustomWithin(separatorWithin)">
                      <v-select
                        :value="separatorWithin"
                        @input="onSeparatorWithin"
                        :items="separatorOptionsWithin"
                        :label="$t('EXPORT_SEPARATOR_WITHIN')"
                      ></v-select>
                    </v-flex>
                    <v-flex sm8 md4 v-if="isSeparatorCustomWithin(separatorWithin)">
                      <v-select
                        :value="$t('EXPORT_SEPARATOR_FREE_CHAR')"
                        @input="onSeparatorWithin"
                        :items="separatorOptionsWithin"
                        :label="$t('EXPORT_SEPARATOR_WITHIN')"
                      ></v-select>
                    </v-flex>
                    <v-flex sm4 md2 v-if="isSeparatorCustomWithin(separatorWithin)">
                      <v-text-field
                        :value="separatorWithin"
                        @input="onSeparatorWithin"
                        :label="$t('EXPORT_SEPARATOR_FREE_CHAR_LABEL')"
                        maxlength="1"
                      ></v-text-field>
                    </v-flex>
                  </v-layout>

                </v-container>
              </v-sheet>
            </v-flex>
          </template>

        </v-layout>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="onSave()" :disabled="!exportConfiguration"><v-icon>mdi-floppy</v-icon> {{ $t('GENERIC_SAVE') }}</v-btn>
        <v-btn text @click="nameDlg=true"><v-icon>mdi-rename-box</v-icon> {{ $t('GENERIC_SAVEAS') }}</v-btn>
        <v-btn text @click="onDelete()" :disabled="!exportConfiguration" color="red darken-1"><v-icon>mdi-trash-can</v-icon> {{ $t('GENERIC_DELETE') }}</v-btn>
        <v-btn text @click="onExport('csv')" :disabled="!exportFields.length"><v-icon>mdi-file-delimited</v-icon> {{ $t('EXPORT_RUN_CSV') }}</v-btn>
        <v-btn text @click="onExport('xlsx')" :disabled="!exportFields.length"><v-icon>mdi-file-excel</v-icon> {{ $t('EXPORT_RUN_EXCEL') }}</v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="nameDlg" persistent max-width="290">
      <v-card>
        <v-card-title class="headline">{{$t('EXPORT_SAVEAS_HEADER')}}</v-card-title>
        <v-card-text>
          <v-text-field
              v-model="exportName"
              :label="$t('SAVEAS_DIALOG_NAME')"
          ></v-text-field>
          <v-alert type="warning" dismissible :value="nameAlert">
            {{nameTextAlert}}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="nameDlg=false;onSaveAs()" :disabled="!exportName || nameAlert">{{$t('SAVEAS_DIALOG_SAVE')}}</v-btn>
          <v-btn color="green darken-1" text @click="nameDlg=false">{{$t('SAVEAS_DIALOG_CANCEL')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <Dialog ref="dialog" />

  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import ExportConfig from '../services/ExportConfig'
import ExportConfigModel from '../models/ExportConfigModel'
import { json2xml } from 'xml-js'
import Selection from './../services/Selection'
import Search from "../services/Search";
import Dialog from "./common/Dialog";

export default {
  name: 'ContactExportDlg',
  components: {
    Dialog,
  },
  props: ['visible'],
  data() {
    return {
      exportConfiguration: null,

      panel: 0,
      groups: {},
      fields: [],
      selectedAvailableFields: [],
      exportFields: [],
      selectedExportFields: [],

      advanced: false,
      encodingOptions: ['UTF-8', 'ISO-8859-15', 'Windows-1252'],
      encoding: 'UTF-8',
      // separatorOptions: ['comma', 'semicolon', 'tabulator', this.$t('EXPORT_SEPARATOR_FREE_CHAR')],
      separatorBetween: 'semicolon',
      separatorWithin: 'comma',

      export_type: null, // TODO: TBD
      category_output: null, // TODO: TBD
      exportName: null,
      nameDlg: false,
      showAlert: false,
      alertText: '',
    }
  },
  computed: {
    ...mapGetters(['user_id', 'data', 'countries', 'exportConfigurationItems', 'activeExportConfig', 'allToExport', 'contactsTotal', 'searchReq', 'exportIds']),
    numSelected() {
      return this.allToExport ? this.contactsTotal - this.exportIds.length : this.exportIds.length
    },
    show: {
      get() {
        return this.visible
      },
      set(val) {
        if (!val) {
          this.$emit('cancel')
        }
      }
    },
    availableFields() {
      return this.fields.map(a => ({ ...a, items: a.items.filter(b => !this.exportFields.map(c => c.value).includes(b.value)) }))
    },
    separatorOptionsBetween() {
      return [
        { value: 'comma', text: this.$t('EXPORT_SEPARATOR_COMMA') },
        { value: 'semicolon', text: this.$t('EXPORT_SEPARATOR_SEMICOLON') },
        { value: 'tabulator', text: this.$t('EXPORT_SEPARATOR_TAB') },
        { value: 'custom', text: this.$t('EXPORT_SEPARATOR_FREE_CHAR') },
      ]
    },
    separatorOptionsWithin() {
      return [
        { value: 'comma', text: this.$t('EXPORT_SEPARATOR_COMMA') },
        { value: 'semicolon', text: this.$t('EXPORT_SEPARATOR_SEMICOLON') },
        { value: 'tabulator', text: this.$t('EXPORT_SEPARATOR_TAB') },
        { value: 'columns', text: this.$t('EXPORT_SEPARATOR_COLUMNS') },
        { value: 'custom', text: this.$t('EXPORT_SEPARATOR_FREE_CHAR') },
      ]
    },
    nameAlert() {
      return (!this.exportFields.length || this.exportConfigurationItems.map( item => item.name ).includes(this.exportName))
    },
    nameTextAlert() {
      if (!this.exportFields.length) {
        return this.$t('EXPORT_INVALID_NOFIELDS')
      } else if (this.exportConfigurationItems.map( item => item.name ).includes(this.exportName)) {
        return this.$t('SAVEAS_DIALOG_NAME_EXISTS')
      } else {
        return ''
      }
    }
  },

  watch: {
    exportConfiguration(val) {
// console.log('exportConfiguration', val)
      if (val) {
        new ExportConfig(this.$api).load(val.id)
          .then(res => {
// console.log('ExportConf: ', res)
            // modelItem = res
            this.selectedAvailableFields = []
            this.exportFields = []
            this.selectedExportFields = []
            const exportFields = []
            this.exportName = val.name

            if (res) {
              this.exportName = res.export_config._attributes.name
              res.export_config.format.column.forEach(a => {
                this.availableFields.forEach(b => {
// console.log(b.items)
                  let item = b.items.find(c => c.type === a._attributes.type && c.id === a._attributes.id)
                  if (item) {
                    exportFields.push(item)
                  }
                })
              })

              const configItem = new ExportConfigModel()
              configItem.fields = exportFields
              configItem.encoding = res.export_config.format._attributes.encoding
              configItem.separatorBetween = res.export_config.format._attributes.column_delimiter
              configItem.separatorWithin = res.export_config.format._attributes.inline_delimiter
              configItem.category_output = res.export_config.format._attributes.category_output
              configItem.export_type = res.export_config.format._attributes.export_type
              this.$store.commit('setActiveExportConfig', configItem)
              this.showAlert = false

            } else {
              this.alertText = 'Wrong export configuration'
              this.showAlert = true
              const configItem = new ExportConfigModel()
              this.$store.commit('setActiveExportConfig', configItem)
            }

          })
      } else {
        this.selectedAvailableFields = []
        this.exportFields = []
        this.selectedExportFields = []
        this.exportName = ''
      }
    },
    activeExportConfig(val) {
// console.log('activeExportConfig', val)
      if (val) {
        this.exportFields = val.fields && Array.isArray(val.fields) ? val.fields : []
        this.encoding = val.encoding || 'UTF-8'
        this.separatorBetween = val.separatorBetween || 'semicolon'
        this.separatorWithin = val.separatorWithin || 'comma'
      }
    },
  },
  mounted() {
    new ExportConfig(this.$api).list() // this is already fixed @ 29.11.2019
    let fields = [], group

    group = {
      id: 'g1',
      icon: 'mdi-account-card-details',
      header: this.$t('CONTACT_STANDARD_FIELDS'),
      items: [
        ...this.data.fields.map(a => ({ ...a, value: `i${a.value}`, label: this.$t(a.text), group: 'g1' })),
        ...this.data.additional_fields.map(a => ({ ...a, value: `a${a.value}`, group: 'g1' }))
      ],
    }
    // replace country field label/text
    const idx = group.items.findIndex(a => a.id === 'country')
    if (idx >= 0) {
      group.items[idx].label= this.$t('EXPORT_FIELD_COUNTRY_ID')
      group.items[idx].text= this.$t('EXPORT_FIELD_COUNTRY_ID')
      group.items.splice(idx, 0, {
        "id": "country_name",
        "value": "country_name",
        "label": this.$t('EXPORT_FIELD_COUNTRY_NAME'),
        "text": this.$t('EXPORT_FIELD_COUNTRY_NAME'),
        "type": "field", group: 'g1'
      })
    }
    // group.items.sort((a, b) => ('' + a.label).localeCompare(b.label))
    fields.push(group)
    this.$set(this.groups, 'g1', true) // is open

    group = {
      id: 'g2',
      icon: 'mdi-account-details',
      header: this.$t('CONTACT_CATEGORIES'),
      items: this.data.categories.map(a => ({ ...a, value: `c${a.value}`, group: 'g2' })),
    }
    group.items.sort((a, b) => ('' + a.label).localeCompare(b.label))
    fields.push(group)
    this.$set(this.groups, 'g2', false) // is closed

    group = {
      id: 'g3',
      icon: 'mdi-account-settings',
      header: this.$t('CONTACT_FREE_FIELDS'),
      items: this.data.custom_fields.map(a => ({ ...a, value: `f${a.value}`, group: 'g3' })),
    }
    group.items.sort((a, b) => ('' + a.label).localeCompare(b.label))
    fields.push(group)
    this.$set(this.groups, 'g3', false) // is closed

    this.fields = fields
  },

  methods: {
    isAvailableSelected(field) {
      return this.selectedAvailableFields.map(a => a.value).indexOf(field.value) >= 0
    },
    onAvailableToggle(field) {
      let idx = this.selectedAvailableFields.map(a => a.value).indexOf(field.value)
      if (idx >= 0) {
        this.selectedAvailableFields.splice(idx, 1)
      } else {
        this.selectedAvailableFields.push(field)
      }
    },
    isExportSelected(field) {
      return this.selectedExportFields.map(a => a.value).indexOf(field.value) >= 0
    },
    onExportToggle(field) {
      let idx = this.selectedExportFields.map(a => a.value).indexOf(field.value)
      if (idx >= 0) {
        this.selectedExportFields.splice(idx, 1)
      } else {
        this.selectedExportFields.push(field)
      }
    },
    onMoveRight() {
      this.exportFields = this.exportFields.concat(this.selectedAvailableFields)
      this.selectedExportFields = this.selectedAvailableFields
      this.selectedAvailableFields = []
    },
    onMoveLeft() {
      this.exportFields = this.exportFields.filter(a => !this.selectedExportFields.includes(a));
      this.selectedAvailableFields = this.selectedExportFields
      this.selectedExportFields = []
    },
    onSeparatorBetween(val) {
// console.log('onSeparatorBetween', val)
      if (val === 'custom') {
        this.separatorBetween = ''
      } else {
        this.separatorBetween = val
      }
    },
    onSeparatorWithin(val) {
// console.log('onSeparatorWithin', val)
      if (val === 'custom') {
        this.separatorWithin = ''
      } else {
        this.separatorWithin = val
      }
    },
    isSeparatorCustomBetween(separator) {
// console.log('isSeparatorCustomBetween', separator)
      return !separator || !this.separatorOptionsBetween.find(a => a.value === separator)
    },
    isSeparatorCustomWithin(separator) {
// console.log('isSeparatorCustomWithin', separator)
      return !separator || !this.separatorOptionsWithin.find(a => a.value === separator)
    },
    onSaveAs() {
      this.exportConfiguration = null
      this.onSave()
    },
    onSave() {
      const jsonObject = {
        format: {
          _attributes: {
            export_type: this.export_type,
            encoding: this.encoding,
            column_delimiter: this.separatorBetween,
            category_output: this.separatorWithin == 'columns'? 'columns' : 'inline',
            inline_delimiter: this.separatorWithin,
          },
          column: []
        }
      }
      for (let field of this.exportFields) {
        jsonObject.format.column.push({
          _attributes: {
            id: field.id,
            header: field.label,
            type: field.type
          }
        })
      }
      new ExportConfig(this.$api).create({
        id: this.exportConfiguration && this.exportConfiguration.id || null,
        name: this.exportName,
        jsonObject})
    },
    onDelete() {
      this.$refs.dialog.show({
        type: '',
        title: this.$t('GENERIC_DELETE_ALERT_TITLE'),
        message: this.$t('EXPORT_DELETE_ALERT', [this.exportConfiguration.name]),
        buttons: [
          {
            label: this.$t('GENERIC_DELETE'),
            close: true,
            // default: true,
            danger: true,
            callback: () => {
              new ExportConfig(this.$api).delete(this.exportConfiguration.id)
                .then(() => {
                  this.exportConfiguration = undefined
                })
            },
          },
          {
            label: this.$t('GENERIC_CANCEL'),
            close: true,
            // default: true,
            // callback: res => {},
          },
        ]
      })
    },
    onExport(type) {
// console.log(type)
      const xml = this.buildXML(type)
// console.log(xml)
      new Selection(this.$api).export(xml)
    },

    /* -------------------
xml = `
<request>
<selection type="list">
  <include>
    <aitem id="41341"/>
  </include>
</selection>
<export_config>
  <format export_type="csv" encoding="UTF-8" column_delimiter="semicolon" category_output="inline" inline_delimiter="comma">
    <column id="email" header="E-Mail" type="field"/>
  </format>
</export_config>
</request>
`
    -------------------*/

    buildXML(type) {
      const jsonObject = {
        request: {
          selection: {
            _attributes: {
              type: this.searchReq.type
            },
            // include: {
            //   aitem: [
            //     // {
            //     //   _attributes: {
            //     //     id: '41341'
            //     //   }
            //     // }
            //   ]
            // },
          },
          export_config: {
            format: {
              _attributes: {
                export_type: type,
                encoding: this.encoding,
                column_delimiter: this.separatorBetween,
                category_output: this.separatorWithin == 'columns'? 'columns' : 'inline',
                inline_delimiter: this.separatorWithin,
              },
              column: [
                // {
                //   _attributes: {
                //     id: 'email',
                //     header: 'E-Mail',
                //     type: 'field'
                //   }
                // }
              ]
            }
          }
        }
      }

      if (this.allToExport) {
        if (this.searchReq.val == 'new' || this.searchReq.val == 'all') {
          jsonObject.request.selection.query = new Search().buildSearchBlockToJS()
        } else if (this.searchReq.type === 'simple') {
          jsonObject.request.selection._attributes['search_string'] = this.searchReq.val
        } else if (this.searchReq.type === 'all') {
          jsonObject.request.selection.query = {
            search_block: {
              search: {
                _attributes: { id: 'id', type: 'field', operator: 'null-not' }
              }
            }
          }
        } else {
          jsonObject.request.selection.query = {
            _attributes: {
              id: this.searchReq.val
            }
          }
        }

        if (this.exportIds.length) {
          jsonObject.request.selection.exclude = {
            aitem: [
              // {
              //   _attributes: {
              //     id: '41341'
              //   }
              // }
            ]
          }
          this.exportIds.forEach(a => {
            jsonObject.request.selection.exclude.aitem.push({
              _attributes: {
                id: a
              }
            })
          });
        }

      } else {
        if (this.searchReq.type === 'simple') {
          jsonObject.request.selection._attributes.type = 'list';
        }
        jsonObject.request.selection.include = {
          aitem: [
            // {
            //   _attributes: {
            //     id: '41341'
            //   }
            // }
          ]
        }
        this.exportIds.forEach(a => {
          jsonObject.request.selection.include.aitem.push({
            _attributes: {
              id: a
            }
          })
        });
      }
      this.exportFields.forEach(a => {
        jsonObject.request.export_config.format.column.push({
          _attributes: {
            id: a.id,
            header: a.label,
            type: a.type
          }
        })
      });
// console.log(jsonObject)
      return json2xml(jsonObject, {compact: true})
    }
  },
}
</script>

<style scoped>
.fields {
  background: none;
}
</style>
